import { expected } from '@aloft/toolkit-shared';
import { Block, Flex, Tabs, Typography } from '@aloft/toolkit-sui';

import { useMemo, useState } from 'react';

import { selectUserType } from '../../../../redux/features/selectors/accountSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { useActiveOrderTabState } from '../../hooks/useActiveOrderTabState.ts';
import { OrderTab, Tkv2OrderManagementDeps } from '../../types.ts';
import { Tkv2OrderCard } from '../OrdersList/Tkv2OrderCard.tsx';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { CreateOrderButton } from './CreateOrderButton.tsx';
import { CreateOrderModal } from './CreateOrderModal.tsx';
import { Tkv2OrderTableHeader } from './Tkv2OrderTableHeader.tsx';
import { TkOrderListField, TkOrderListing, Tkv2SortState } from './types.ts';
import { sortTkOrderListings } from './utils/sortTkOrderListings.ts';

type Props = {
  ordersByTab: Record<string, TkOrderListing[]>;
  getOpenOrderRoute: Tkv2OrderManagementDeps['getOpenOrderRoute'];
  isViewingArchived: boolean;
  isViewingTraining: boolean;
  isViewingCompleted: boolean;
  isViewingPending: boolean;
};

export const Tkv2OrderTable = ({
  ordersByTab,
  getOpenOrderRoute,
  isViewingArchived,
  isViewingTraining,
  isViewingCompleted,
  isViewingPending,
}: Props) => {
  const [activeTab, setActiveTab] = useActiveOrderTabState();
  const [createOrderOpen, setCreateOrderOpen] = useState<boolean>(false);
  const [sortState, setSortState] = useState<Tkv2SortState>({
    field: 'effectiveDate',
    dir: 'desc',
  });
  const orders = ordersByTab[activeTab] || [];
  const userType = useAppSelector(selectUserType);
  const showAppraisifyTabs = userType === 'appraisify';
  const tabs: { label: string; value: OrderTab }[] = [
    {
      label: `Active ${ordersByTab.active?.length ? `(${ordersByTab.active.length})` : ''}`,
      value: 'active',
    },
    ...(showAppraisifyTabs
      ? [
          {
            label: `Pending ${ordersByTab.pending?.length ? `(${ordersByTab.pending.length})` : ''}`,
            value: 'pending' as const,
          },
        ]
      : []),
    {
      label: `Training ${ordersByTab.training?.length ? `(${ordersByTab.training.length})` : ''}`,
      value: 'training',
    },
    ...(showAppraisifyTabs ? [{ label: 'Completed', value: 'completed' as const }] : []),
    { label: 'Archived', value: 'archived' },
  ];

  const sortedOrders = useMemo(() => sortTkOrderListings(orders, sortState), [orders, sortState]);

  const handleHeaderClick = (field: TkOrderListField) => {
    setSortState((prevState) => {
      let dir: Tkv2SortState['dir'] = 'asc';
      if (prevState.field === field && prevState.dir === 'asc') {
        dir = 'desc';
      }

      return {
        field,
        dir,
      };
    });
  };

  const orderListIsEmpty = sortedOrders.length === 0 && !isViewingArchived;
  const archivedListIsEmpty = sortedOrders.length === 0 && isViewingArchived;

  return (
    <Flex className="tkv2-order-table">
      <Flex paddingLeft="3rem" paddingBottom="1rem" backgroundColor="surface1">
        <Tabs
          variant="secondary"
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </Flex>
      {isViewingArchived === false &&
        isViewingTraining === false &&
        isViewingCompleted === false &&
        isViewingPending === false && (
          <CreateOrderButton
            isTooltipAlwaysOpen={sortedOrders.length === 0}
            onClick={() => setCreateOrderOpen(true)}
          />
        )}
      <Tkv2OrderTableHeader sortState={sortState} handleHeaderClick={handleHeaderClick} />
      <Flex id="orders-list" data-testid="orders-list" className="orders-list">
        {sortedOrders.length > 0 &&
          sortedOrders.map((order) => (
            <Tkv2OrderCard
              key={order.id}
              order={order}
              to={getOpenOrderRoute(
                order.type,
                order.requiresPrefill || false,
                expected(order.id),
                order?.inspectifyOrder || false,
              )}
              updateOrder={() => order.toggleArchive(!order.isArchived)}
              deleteOrder={order.deleteOrder}
              downloadReport={order.downloadReport}
              openDocumentsPage={order.openDocumentsPage}
            />
          ))}

        {orderListIsEmpty && (
          <Flex className="empty-orders-list">
            <Typography variant="h1" __dangerDoNotUseSx={{ fontSize: '60px' }}>
              Welcome to Appraisify Toolkit!
            </Typography>
            <Typography
              variant="h2"
              color="textTertiary"
              __dangerDoNotUseSx={{ fontSize: '20px', fontWeight: 500 }}
            >
              {userType === 'appraisify'
                ? `Your new assignments will appear here. ${ordersByTab.training?.length ? 'A training order has been assigned to you to familiarize yourself with the system. Please review it by accessing the Training tab.' : ''}`
                : 'Support your adjustments with cost and market data'}
            </Typography>
          </Flex>
        )}

        {archivedListIsEmpty && (
          <Block className="empty-archived-orders-list">
            <Typography>You have no archived orders.</Typography>
          </Block>
        )}
      </Flex>
      <CreateOrderModal isOpen={createOrderOpen} onClose={() => setCreateOrderOpen(false)} />
    </Flex>
  );
};
